<template>
  <div class="h-screen flex overflow-hidden bg-base-200">
    <!-- Sidebar container (reserves space on desktop) -->
    <div
      class="sidebar-container shrink-0 transition-all duration-300"
      :class="sidebarOpen ? 'w-72' : 'w-0'"
    >
      <aside
        class="fixed inset-y-0 left-0 z-50 w-72 bg-base-100 shadow-lg flex flex-col transition-transform duration-300"
        :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'"
      >
        <!-- Sidebar Header -->
        <div
          class="h-16 flex items-center justify-center px-4 border-b border-base-300 shrink-0 relative"
        >
          <img
            src="/img/roadgis-logo.svg"
            alt="TripCare"
            class="w-28 dark:invert"
          />
          <span
            @click="sidebarOpen = false"
            class="lg:hidden absolute right-4 cursor-pointer hover:opacity-70"
          >
            <DefaultIcon name="xMark" size="w-6 h-6" />
          </span>
        </div>

        <!-- Navigation Menu -->
        <nav class="flex-1 overflow-y-auto">
          <ul class="menu w-full">
            <li v-for="(group, index) in navigation" :key="index">
              <details :open="group.open !== false" class="my-1">
                <summary class="flex items-center gap-2">
                  <DefaultIcon
                    v-if="group.icon"
                    :name="group.icon"
                    size="w-4 h-4"
                  />
                  <span class="text-xs font-semibold uppercase tracking-wider">
                    {{ group.name }}
                  </span>
                </summary>
                <ul>
                  <li
                    v-for="(item, idx) in group.items"
                    :key="idx"
                    class="my-1"
                  >
                    <router-link
                      :to="item.path"
                      @click="closeSidebarOnMobile"
                      active-class="text-primary font-semibold bg-base-300 shadow-none"
                    >
                      <DefaultIcon
                        v-if="item.icon"
                        :name="item.icon"
                        size="w-4 h-4"
                      />
                      {{ item.name }}
                    </router-link>
                  </li>
                </ul>
              </details>
            </li>
          </ul>
        </nav>

        <!-- Sidebar Footer -->
        <div class="border-t border-base-300 shrink-0">
          <template v-if="authStore.user">
            <!-- Mode switcher (drivers only) -->
            <div
              v-if="authStore.user.role === 'driver' || isMobile"
              class="px-3 pt-3 pb-2 space-y-0.5"
            >
              <router-link
                to="/"
                class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-base-content/60 hover:bg-base-200 hover:text-base-content transition-colors"
                active-class="bg-base-200 text-base-content font-medium"
              >
                <DefaultIcon name="plan" size="w-4 h-4" />
                Planuotojo aplinka
              </router-link>
              <router-link
                to="/driving"
                @click="closeSidebarOnMobile"
                class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-base-content/60 hover:bg-base-200 hover:text-base-content transition-colors"
                active-class="bg-base-200 text-base-content font-medium"
              >
                <DefaultIcon name="driving" size="w-4 h-4" />
                Vairuotojo aplinka
              </router-link>
            </div>

            <!-- User row -->
            <div
              class="flex items-center gap-3 px-4 py-3 border-t border-base-200"
            >
              <div class="avatar avatar-online avatar-placeholder">
                <div class="bg-neutral text-neutral-content w-8 rounded-full">
                  <span class="text-xs font-semibold">{{
                    authStore.user.initials
                  }}</span>
                </div>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-medium truncate">
                    {{ authStore.user.name }} {{ authStore.user.surname }}
                  </p>
                  <span
                    v-if="appEnvironment === 'dev'"
                    class="badge badge-warning badge-xs shrink-0"
                    >Test</span
                  >
                </div>
                <p class="text-xs text-base-content/60">
                  {{ roleTranslations[authStore.user.role] }}
                </p>
              </div>
              <button
                @click="handleLogout"
                class="btn btn-ghost btn-sm btn-square text-base-content/40 hover:text-error"
                title="Atsijungti"
              >
                <DefaultIcon name="logOut" size="w-4 h-4" />
              </button>
            </div>
          </template>
          <div v-else class="p-4">
            <span class="loading loading-spinner loading-sm"></span>
          </div>
        </div>
      </aside>
    </div>

    <!-- Mobile overlay -->
    <div
      v-if="sidebarOpen && isMobile"
      class="fixed inset-0 bg-black/50 z-40"
      @click="sidebarOpen = false"
    />

    <!-- Main Content -->
    <div class="flex-1 flex flex-col min-w-0 overflow-hidden">
      <!-- Top Navbar -->
      <div
        class="navbar bg-base-100 border-b border-base-300 px-4 lg:px-8 shrink-0"
      >
        <div class="flex-none">
          <span
            @click="sidebarOpen = !sidebarOpen"
            class="cursor-pointer hover:opacity-70"
            aria-label="Toggle sidebar"
          >
            <DefaultIcon name="menu" size="w-6 h-6" />
          </span>
        </div>
        <div class="flex-1"></div>
        <div class="flex-none flex items-center gap-2">
          <p class="text-sm text-base-content/60 hidden md:block">
            {{ getTodayDate() }}
          </p>
          <router-link
            to="/trips?status=tripStatus6"
            class="btn btn-ghost btn-circle"
          >
            <DefaultIcon name="notification" size="w-6 h-6" />
          </router-link>
          <div class="avatar avatar-placeholder">
            <div class="bg-neutral text-neutral-content w-9 rounded-full">
              <span v-if="authStore.user" class="font-semibold text-sm">{{
                authStore.user.initials
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Page Content -->
      <div class="flex-1 overflow-y-auto p-4 lg:px-8">
        <router-view></router-view>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useTimeManagement } from "@/composables/time/useTimeManagement";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";

const router = useRouter();
const authStore = useAuthStore();

const isMobile = ref(window.innerWidth < 1024);
const sidebarOpen = ref(window.innerWidth >= 1024);

const { getTodayDate } = useTimeManagement();

function closeSidebarOnMobile() {
  if (isMobile.value) {
    sidebarOpen.value = false;
  }
}

function checkScreenSize() {
  isMobile.value = window.innerWidth < 1024;
}

onMounted(() => {
  checkScreenSize();
  window.addEventListener("resize", checkScreenSize);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkScreenSize);
});

async function handleLogout() {
  try {
    await authStore.logout();
    router.push("/login");
  } catch (error) {
    console.error("Logout failed:", error);
  }
}

const roleTranslations = {
  planner: "Planuotojas",
  admin: "Administratorius",
  driver: "Vairuotojas",
};

const appEnvironment = computed(() => process.env.VUE_APP_MODE);

const navigation = [
  {
    name: "Planavimas",
    icon: "plan",
    open: true,
    items: [
      { name: "Kelionės", path: "/travels" },
      { name: "Pavėžėjimų užsakymai", path: "/trips" },
      { name: "Neregistruoti pavėžėjimai", path: "/unregistered-trips" },
    ],
  },
  {
    name: "Administravimas",
    icon: "settings",
    open: true,
    items: [
      { name: "Transportas", path: "/vehicles" },
      { name: "Klientai", path: "/clients" },
      { name: "Adresai", path: "/addresses" },
      { name: "Vartotojai", path: "/users" },
    ],
  },
  {
    name: "Pagalba",
    icon: "help",
    open: false,
    items: [{ name: "Dokumentacija", path: "/support/documentation" }],
  },
];
</script>

<style>
::-webkit-scrollbar {
  width: 0.375rem !important;
}
::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px #cbd5e1;
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
</style>
